import { z } from 'zod';
import { SignalRevisionObservationSchema } from './capture.js';

/**
 * Passive Signal Capture v1 — the wire contract.
 *
 * Spec:     docs/tasks/passive-signal-capture-v1-spec.md §R2, §Wire contract
 * Security: docs/security-reviews/passive-signal-capture-v1-design-security-review.md
 *           (C-2, SEC-4, SEC-5, SEC-9, SEC-10)
 *
 * ── Read this before adding a field ─────────────────────────────────────────
 *
 * The product claim this schema carries is: *no source code, prompts, tool
 * inputs/outputs or cleartext file paths ever leave the developer's machine.*
 * That claim is not enforced by review, by a filter, or by a list of forbidden
 * field names. It is enforced by making free text **unrepresentable**:
 *
 *   1. Every object node is strict. An unknown key is a 400, not a silent strip.
 *   2. Every string leaf is exactly one of four forms, and there is no fifth:
 *        - z.enum([...])                  closed vocabulary
 *        - z.uuid()                       identifier
 *        - /^[a-f0-9]{64}$/               bare keyed digest (no "sha256:" prefix —
 *                                         a prefixed form invites a free-text prefix)
 *        - MODEL_PATTERN                  the one bounded-regex exception
 *   3. No z.record / z.any / z.unknown anywhere in the tree.
 *   4. Arrays are capped — an array is a bulk channel.
 *
 * `apps/api/src/skill-signals/__tests__/schema-introspection.spec.ts` walks this
 * tree via `z.toJSONSchema()` and fails on the *category* of mistake. It knows
 * no field names, so it needs no maintenance when a field is added — and it
 * cannot be satisfied by an author forgetting to update a list. **If it rejects
 * your field, change this schema. Never relax that test.**
 *
 * ── Consequence of strictness: every addition is breaking (D-5) ──────────────
 *
 * The usual "add new fields as optional for forward compatibility" convention
 * does NOT apply here. Under `.strict()` the server rejects any field it does
 * not know, so a CLI that sends a new field talks to no deployed server. Every
 * schema change therefore requires a `schemaVersion` bump and re-consent
 * (SEC-28.7). The field set is genuinely versioned. This is the correct trade
 * for this feature but must be understood before someone adds a field in a
 * patch release.
 *
 * ── What is deliberately absent ─────────────────────────────────────────────
 *
 * No `workspaceId`, no `agentId`  — derived from the API-key principal (SEC-1).
 * No `skillSlug`                  — a developer-authored slug is free text and
 *                                   semantically rich (`layoff-comms`,
 *                                   `acquisition-dd` disclose subject matter to
 *                                   a hostile admin). Skill identity is the
 *                                   numeric id, resolved client-side (D-1/H-2).
 * No timestamp                    — server-assigned and hour-truncated (SEC-22).
 *                                   Minute precision reconstructs attendance,
 *                                   breaks, weekend and night work, and by
 *                                   absence sickness or interviewing.
 *
 * Their absence from the schema *is* the enforcement.
 */

/* #region signal-wire-contract -- GENERATOR SOURCE. Everything between this
 * marker and `#endregion signal-wire-contract` is copied VERBATIM into
 * `packages/cli/src/capture/signal-schema.ts` by
 * `scripts/generate-cli-signal-schema.mjs`, and CI fails if the generated file
 * is out of date (`pnpm lint:signal-schema`).
 *
 * The CLI cannot import this package at runtime — `@skillspec/cli` is published
 * to npm and `@skillspec/shared-types` is private — so SEC-11's "validate
 * against the identical schema" needs a copy. Generation makes drift
 * structurally impossible rather than detected after the fact; a JSON-Schema
 * parity test cannot see `.superRefine()` at all, so the two cross-field rules
 * below were previously able to diverge with every test green.
 *
 * Consequence for editors: this region must not reference anything declared
 * outside it, and must not import anything beyond `zod`.
 */

/** A bare 64-char lowercase hex digest — HMAC-SHA256 output, no prefix. */
export const HEX64_PATTERN = /^[a-f0-9]{64}$/;

/**
 * The single bounded-regex exception (SEC-5). The model vocabulary is owned by
 * the Claude Code runtime and changes outside our release cycle, so it cannot be
 * a closed enum without a new model id causing a 400 on every submission.
 */
/* eslint-disable-next-line no-useless-escape -- the `\-` escape is load-bearing: `z.toJSONSchema()` emits `RegExp.source` verbatim and the schema-introspection meta-test pins the emitted pattern exactly. Dropping the backslash breaks the SEC-5 control. */
export const MODEL_PATTERN = /^[a-z0-9][a-z0-9.\-]{0,63}$/;

/** Wire-level attribution tier. Lowercase; the DB enum is uppercase. */
export const SIGNAL_ATTRIBUTION_VALUES = ['high', 'medium', 'low', 'ambiguous'] as const;

/** Claude Code permission modes. Closed vocabulary (SEC-5). */
export const SIGNAL_PERMISSION_MODES = [
  'default',
  'plan',
  'acceptEdits',
  'bypassPermissions',
] as const;

/** Reasoning-effort levels. Closed vocabulary (SEC-5). */
export const SIGNAL_EFFORT_LEVELS = ['low', 'medium', 'high'] as const;

/** Current wire schema version. A bump forces re-consent (SEC-28.7). */
export const SIGNAL_SCHEMA_VERSION = 1;

/** Server-side clamp bound for every count field (SEC-8). */
export const SIGNAL_COUNT_MAX = 10000;

/** Maximum attribution window, in turns. */
export const SIGNAL_WINDOW_TURNS_MAX = 64;

/** SEC-10 — arrays are a bulk channel. */
export const SIGNAL_CO_ACTIVE_SKILLS_MAX = 8;

const digest = () => z.string().regex(HEX64_PATTERN);
const count = () => z.number().int().min(0).max(SIGNAL_COUNT_MAX);
const rate = () => z.number().min(0).max(1);

/**
 * `POST /v1/internal/skill-signals` request body.
 *
 * `z.strictObject` (not `z.object`) is load-bearing: a plain `z.object` strips
 * unknown keys **silently**, which would turn a rogue-key submission into a
 * 200 and leave the strip-and-warn log — the only detection mechanism — never
 * firing. This is the specific gap the security review found in the
 * `audit-ingest` reference implementation (C-2).
 */
export const SignalSessionPayloadSchema = z
  .strictObject({
    /**
     * Pinned literal, not a range. An unknown version is a 400 carrying a
     * re-consent hint rather than a best-effort parse against a field set the
     * developer never consented to.
     */
    schemaVersion: z.literal(SIGNAL_SCHEMA_VERSION),

    sessionId: z.uuid(),
    /**
     * Derived client-side as a keyed digest of the runtime's prompt id, folded
     * into UUID form. The runtime's own prompt id is never sent: it is an
     * opaque, unvalidated string from outside our trust boundary and therefore
     * a free-text channel.
     */
    activationPromptId: z.uuid(),

    /** Numeric id, resolved to a workspace skill server-side (SEC-30). */
    skillId: z.number().int().positive(),

    /**
     * Optional: the CLI can only compute this when it can read the skill file
     * locally (a skill invoked from a managed or plugin directory may not be
     * readable). Omitted rather than fabricated — a stand-in digest would
     * silently break server-side edit detection, which is the field's only
     * purpose.
     *
     * ⚠ **NEVER POPULATED IN v1.** `flushSession` builds its `DeriveContext`
     * without a `resolveSkillContentHash`, so this is always absent on the wire
     * and the column is always NULL. Skill-edit detection does not exist and
     * must not be claimed — see §Out of Scope in
     * `docs/tasks/passive-signal-capture-v1-spec.md`.
     *
     * ── Why it is unpopulated (reason updated 2026-08-01 — read this) ───────
     *
     * The reason recorded here previously was the cross-developer correlation
     * risk of a plain `sha256(content)`. **That reason is withdrawn: it was
     * wrong, and the record must match the actual reason.** The Security
     * Reviewer ruled (§6 of the design security review) that plain, unsalted
     * `sha256(content)` is the CORRECT construction:
     *
     *  - `Skill` is workspace-scoped and the admin already holds the content,
     *    so there is no private-side value for a salt to protect. The H-1
     *    analogy to `repoIdHash` does not transfer.
     *  - The identical construction already ships in production as
     *    `Skill.sourceHash` (`skill-parser.service.ts:65-71`).
     *  - HMAC-under-salt would be affirmatively WRONG, not merely cautious: the
     *    salt is per-installation, so an unchanged skill would yield N distinct
     *    digests across N developers, making every new participant look like an
     *    edit event and injecting false discontinuities into the version-change
     *    analysis that is the statistical layer's primary bias defence.
     *
     * The actual reason it stays unpopulated is **a stakeholder deferral to the
     * next cycle**, carrying the conditions the ruling attached:
     *
     *  - **SEC-32** — construction and normalisation must match the server's
     *    `computeSkillHash` exactly, from one shared source, with a test
     *    asserting CLI and server agree on identical input.
     *  - **SEC-33** — the genuinely new risk, and the one under-weighted first
     *    time round: hashing puts the capture pipeline in contact with file
     *    contents for the first time. Reads must be confined to an allowlist of
     *    skill roots, path-checked after `realpath`, size-capped, never
     *    journaled or logged, and omitted rather than fabricated on failure.
     *  - **SEC-34** — the digest must not be readable on any path: no response
     *    DTO, no `groupBy` dimension, not filterable.
     *
     * Do not populate this field without those three. Until then the warning
     * above stands and no server-side edit detection may be claimed.
     *
     * The field is kept rather than removed because the schema is versioned
     * under `.strict()`: adding it later is a breaking change requiring a
     * `schemaVersion` bump and re-consent (SEC-28.7), so removing it now would
     * cost a forced re-consent to undo.
     */
    skillContentHash: digest().optional(),

    attribution: z.enum(SIGNAL_ATTRIBUTION_VALUES),
    coActiveSkills: z.array(z.number().int().positive()).max(SIGNAL_CO_ACTIVE_SKILLS_MAX),
    windowTurns: z.number().int().min(0).max(SIGNAL_WINDOW_TURNS_MAX),
    windowCompacted: z.boolean(),

    model: z.string().regex(MODEL_PATTERN),
    permissionMode: z.enum(SIGNAL_PERMISSION_MODES),
    /**
     * HMAC of the **repo root** under a per-developer salt. Omitted entirely
     * when no repo root can be determined — never an absolute-path digest,
     * which typically embeds the OS username (SEC-14).
     */
    repoIdHash: digest().optional(),
    effortLevel: z.enum(SIGNAL_EFFORT_LEVELS),
    toolCallCount: count(),
    distinctPathCount: count(),

    toolFailureRate: rate(),
    toolErrorRate: rate(),
    permissionPromptCount: count(),
    abandoned: z.boolean(),
  })
  .superRefine((value, ctx) => {
    // A nonzero rate over zero tool calls is arithmetically impossible. It
    // indicates either a derivation bug or a hand-crafted payload, and both
    // should be rejected rather than averaged into a regression verdict.
    if (value.toolCallCount === 0) {
      for (const field of ['toolFailureRate', 'toolErrorRate'] as const) {
        if (value[field] !== 0) {
          ctx.addIssue({
            code: 'custom',
            path: [field],
            message: `${field} must be 0 when toolCallCount is 0`,
          });
        }
      }
    }
    // The window cannot contain more distinct paths than tool calls.
    if (value.distinctPathCount > value.toolCallCount) {
      ctx.addIssue({
        code: 'custom',
        path: ['distinctPathCount'],
        message: 'distinctPathCount cannot exceed toolCallCount',
      });
    }
  });

export type SignalSessionPayload = z.infer<typeof SignalSessionPayloadSchema>;

/**
 * `POST /v1/internal/skill-signals` response.
 *
 * `id` is a decimal **string**, never a JS number: the column is a BigInt and
 * ids beyond 2^53 would silently lose precision on the wire.
 * `recordedAt` is hour-truncated UTC (SEC-22).
 */
export interface SignalSessionAcceptedResponse {
  id: string;
  recordedAt: string;
  created: boolean;
}

/* #endregion signal-wire-contract */

/**
 * Staged Wave 1 signal-v2 parser. The active CLI constant remains v1 until the
 * reviewed rollout advances; API consumers may parse this alongside v1 first.
 */
export const SIGNAL_SCHEMA_V2_VERSION = 2 as const;

export const SignalSessionPayloadV2Schema = z
  .strictObject({
    schemaVersion: z.literal(SIGNAL_SCHEMA_V2_VERSION),
    sessionId: z.uuid(),
    activationPromptId: z.uuid(),
    skillId: z.number().int().positive(),
    revision: SignalRevisionObservationSchema,
    attribution: z.enum(SIGNAL_ATTRIBUTION_VALUES),
    coActiveSkills: z.array(z.number().int().positive()).max(SIGNAL_CO_ACTIVE_SKILLS_MAX),
    windowTurns: z.number().int().min(0).max(SIGNAL_WINDOW_TURNS_MAX),
    windowCompacted: z.boolean(),
    model: z.string().regex(MODEL_PATTERN),
    permissionMode: z.enum(SIGNAL_PERMISSION_MODES),
    repoIdHash: digest().optional(),
    effortLevel: z.enum(SIGNAL_EFFORT_LEVELS),
    toolCallCount: count(),
    distinctPathCount: count(),
    toolFailureRate: rate(),
    toolErrorRate: rate(),
    permissionPromptCount: count(),
    abandoned: z.boolean(),
  })
  .superRefine((value, ctx) => {
    if (value.toolCallCount === 0) {
      for (const field of ['toolFailureRate', 'toolErrorRate'] as const) {
        if (value[field] !== 0) {
          ctx.addIssue({
            code: 'custom',
            path: [field],
            message: `${field} must be 0 when toolCallCount is 0`,
          });
        }
      }
    }
    if (value.distinctPathCount > value.toolCallCount) {
      ctx.addIssue({
        code: 'custom',
        path: ['distinctPathCount'],
        message: 'distinctPathCount cannot exceed toolCallCount',
      });
    }
  });

export type SignalSessionPayloadV2 = z.infer<typeof SignalSessionPayloadV2Schema>;

/** API-side staged parser: strict v1 and strict v2, with no best-effort branch. */
export const SignalSessionPayloadAnySchema = z.union([
  SignalSessionPayloadSchema,
  SignalSessionPayloadV2Schema,
]);
export type SignalSessionPayloadAny = z.infer<typeof SignalSessionPayloadAnySchema>;

/** `GET /v1/skill-signals/aggregate` — k-anonymised (SEC-23). */
export interface SignalCusumStatistics {
  baselineFailureRate: number;
  cumulativeDeviation: number;
  thresholdBreached: boolean;
}

export interface SignalModelChangeStatistics {
  fromModel: string;
  toModel: string;
  pValue: number;
  regressionDetected: boolean;
}

export interface SignalStatistics {
  cusum?: SignalCusumStatistics;
  modelChange?: SignalModelChangeStatistics;
}

export const SIGNAL_STATISTICS_UNAVAILABLE_REASONS = ['series_too_large'] as const;
export type SignalStatisticsUnavailableReason =
  (typeof SIGNAL_STATISTICS_UNAVAILABLE_REASONS)[number];

export interface SignalAggregateResponse {
  skillId: number;
  /**
   * True when **either** minimum is unmet — fewer than 5 contributing sessions
   * **or** fewer than 3 distinct contributors (SEC-23). The gate is disjunctive:
   * a cell is released only if it clears both floors.
   *
   * A suppressed cell carries no metric values **and no underlying counts** —
   * `sessionCount`/`contributorCount` are absent, because "100 sessions, 1
   * contributor" is itself the per-developer volume the floor exists to hide.
   */
  suppressed: boolean;
  sessionCount?: number;
  contributorCount?: number;
  avgToolFailureRate?: number;
  avgToolErrorRate?: number;
  /** Why advisory inference is unavailable despite the aggregate being released. */
  statisticsUnavailableReason?: SignalStatisticsUnavailableReason;
  /**
   * Privacy-qualified, advisory inference derived on this read. Absent when
   * the relevant hourly/model cohorts do not independently clear their gates.
   * No contributor-level values or cohort counts are exposed.
   */
  statistics?: SignalStatistics;
}

/**
 * `GET /v1/skill-signals/participation` — ADMIN/OWNER only.
 *
 * Boolean opt-in status **only**. Session counts or activity volume per person
 * would turn a consent indicator into a timesheet (SEC-25 / H-2).
 *
 * `optedIn` is derived from the developer's recorded consent (the API key's
 * signal installation binding), **not** from whether they have submitted any
 * rows. Deriving it from rows would make presence a weak volume signal and
 * would make an opted-in developer who did no attributable work
 * indistinguishable from one who never opted in — see QA OBS-1.
 *
 * Only opted-in agents are returned. An absent agent has not opted in.
 */
export const SignalParticipationResponseSchema = z.strictObject({
  agentId: z.number().int().positive(),
  agentName: z.string(),
  optedIn: z.boolean(),
});

export type SignalParticipationResponse = z.infer<typeof SignalParticipationResponseSchema>;
